/**
 * SitePick front — 注文専用チャット（/chat/{id}/）の画面挙動（docs/09 §4d）。
 *
 * メッセージアプリの作法に合わせる（2026-09-12 人間指示）:
 *  - 開いたら最新のメッセージ（最下部）が見える位置から始める
 *  - 見出しは上に、入力バーは下に固定（CSS の sticky。ここでは高さだけ CSS 変数へ渡す）
 *  - 入力欄は 1 行から始めて、内容に応じて最大 6 行まで自動で伸びる
 *  - クリップのアイコンでファイルを選び、選んだ名前を小さく表示する
 *  - Cmd/Ctrl + Enter で送信
 *
 * 送信自体は通常のフォーム POST（サーバーで nonce / session / Origin を検証）。JS が失敗しても
 * フォームはそのまま使える（progressive enhancement）。
 */

const MAX_ROWS = 6;

/**
 * サイトヘッダーと入力バーの実高さを CSS 変数へ（sticky の位置と履歴下端の余白に使う）。
 *
 * @param {HTMLElement|null} form
 */
function syncHeights(form) {
  const header = document.querySelector(".l-header");
  const root = document.documentElement;
  root.style.setProperty("--sp-header-h", header ? `${Math.round(header.getBoundingClientRect().height)}px` : "0px");
  if (form) {
    root.style.setProperty("--sp-chat-bar-h", `${Math.round(form.getBoundingClientRect().height)}px`);
  }
}

/**
 * 入力欄を内容に合わせて伸縮させる（1〜MAX_ROWS 行）。
 *
 * @param {HTMLTextAreaElement} input
 * @param {HTMLElement} form
 */
function autoGrow(input, form) {
  const line = parseFloat(getComputedStyle(input).lineHeight) || 22;
  const pad = input.offsetHeight - input.clientHeight; // border
  input.style.height = "auto";
  const max = line * MAX_ROWS + 20 + pad;
  input.style.height = `${Math.min(input.scrollHeight + pad, max)}px`;
  syncHeights(form);
}

/**
 * 最下部（最新メッセージ + 入力バー）へ移動する。
 */
function scrollToBottom() {
  const el = document.scrollingElement || document.documentElement;
  el.scrollTop = el.scrollHeight;
}

/**
 * 初期表示を最下部に貼り付ける。
 *
 * 1 回 scrollTo するだけでは足りない: フォント・画像・lazy 画像の読み込みで高さが後から
 * 伸びるし、iOS Safari は再読み込み（送信後の PRG）で前回のスクロール位置を復元して上書きする。
 * そこで復元を止め、読み込み後しばらくは高さの変化に追従して最下部を保つ。
 * 利用者が自分でスクロール（wheel / touch / キー操作）したら追従をやめる。
 */
function pinToBottom() {
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }
  let pinned = true;
  const unpin = () => {
    pinned = false;
  };
  for (const ev of ["wheel", "touchstart", "keydown"]) {
    window.addEventListener(ev, unpin, { passive: true, once: true });
  }
  const settle = () => {
    if (pinned) {
      scrollToBottom();
    }
  };
  settle();
  requestAnimationFrame(settle);
  window.addEventListener("load", () => {
    settle();
    requestAnimationFrame(settle);
  });
  if (typeof ResizeObserver === "function") {
    const ro = new ResizeObserver(settle);
    ro.observe(document.body);
    window.setTimeout(() => ro.disconnect(), 5000);
  }
  document.querySelectorAll(".p-chat__timeline img").forEach((img) => {
    img.addEventListener("load", settle, { once: true });
  });
}

/**
 * 選択したファイル名を表示する。
 *
 * @param {HTMLInputElement} files
 * @param {HTMLElement|null} picked
 */
function showPicked(files, picked) {
  if (!picked) {
    return;
  }
  const names = Array.from(files.files || []).map((f) => f.name);
  if (names.length === 0) {
    picked.hidden = true;
    picked.textContent = "";
    return;
  }
  picked.hidden = false;
  picked.textContent = `添付: ${names.join(" / ")}`;
}

export function initChat() {
  const page = document.querySelector(".p-chat");
  if (!page) {
    return;
  }
  const form = page.querySelector(".js-chat-form");
  const input = page.querySelector(".js-chat-input");
  const files = page.querySelector(".js-chat-files");
  const picked = page.querySelector(".js-chat-picked");

  syncHeights(form);
  window.addEventListener("resize", () => syncHeights(form), { passive: true });

  if (input instanceof HTMLTextAreaElement && form) {
    autoGrow(input, form);
    input.addEventListener("input", () => autoGrow(input, form));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (typeof form.requestSubmit === "function") {
          form.requestSubmit();
        } else {
          form.submit();
        }
      }
    });
  }

  if (files instanceof HTMLInputElement) {
    files.addEventListener("change", () => {
      showPicked(files, picked);
      syncHeights(form);
    });
    showPicked(files, picked);
  }

  // 履歴は最新（最下部）から。
  window.addEventListener("load", () => syncHeights(form));
  pinToBottom();
}
